import { connect, useDispatch } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { FieldArray, FieldArrayRenderProps } from 'react-final-form-arrays';
import { FormSpy } from 'react-final-form';
import { ARRAY_ERROR } from 'final-form';
import cn from 'classnames';

import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { Button } from 'UIPackage/MainModule/components/Button';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { KnmCollapse } from 'KnmPackage/MainModule/components/KnmCollapse';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import {
  getKnmDataVersion,
  getKnmFormRef,
  getKnmIsNeedActSaveOrSign,
  getKnmStatusCode,
} from 'KnmPackage/MainModule/selectors/knmSelectors';
import { Row } from 'UIPackage/FormModule/components/Row';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { DatePickerField } from 'UIPackage/MainModule/components/DatePickerField';
import { getUserRoleIsSupervisor } from 'AuthPackage/MainModule/selectors/authSelectors';
import { KnmErpStatusCodesEnum } from 'APIPackage/CatalogsModule/enums/KnmErpStatusCodesEnum';
import { KnmErknmStatusCodesEnum } from 'APIPackage/CatalogsModule/enums/KnmErknmStatusCodesEnum';
import { IDictionarySelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import { IEventType } from 'APIPackage/DictionariesModule/interfaces/IEventType';
import { getCatalogsInteractionCategorySelectOptions } from 'CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { IInteractionCategorySelectOption } from 'CorePackage/CatalogsModule/interfaces/IInteractionCategorySelectOption';
import { IDictionaryEventTypeSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryEventTypeSelectOption';
import { IKnmPmForm } from 'KnmPmPackage/MainModule/interfaces/IKnmPmForm';
import { catalogGet } from 'CorePackage/CatalogsModule/actions/catalogsActions';
import { CommonCatalogsNamesEnum } from 'CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { IKnmErknmFormEvent } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormEvent';
import { IKnmPmFormRescheduling } from 'KnmPmPackage/MainModule/interfaces/IKnmPmFormRescheduling';
import { getEventBlockId } from 'KnmPmPackage/MainModule/constants/knmPmFieldsElementsIds';
import {
  getKnmPmProfEventTypesSelectOptions,
  getPmStartDate,
  getPmStopDate,
} from 'KnmPmPackage/MainModule/selectors/knmPmSelectors';
import { PM_EVENTS } from 'CorePackage/MainModule/routes/routes';
import { isLaterThanAdminSetting } from 'CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { AdminSettingCodesEnum } from 'APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { getAdminFetchSettings } from 'PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { IAdminSetting } from 'APIPackage/AdminModule/interfaces/IAdminSetting';
import { KnmEventsDocuments } from 'KnmPackage/KnmEventsDocuments/components/KnmEventsDocuments';
import { InspectionResultActionTypeEnum } from 'APIPackage/CatalogsModule/enums/InspectionResultActionTypeEnum';
import { parseDate } from 'CorePackage/MainModule/common/formatDate';
import { DATE_FORMAT_LONG_FNS } from 'CorePackage/MainModule/constants/dateFormats';
import { IKnmForm } from 'KnmPackage/MainModule/interfaces/IKnmForm';

import styles from './PmEvents.module.scss';
import { getTitleFieldWithRedStar } from '../../../functions/getTitleFieldWithRedStar';
import { checkHasErrorInCollapseBlock } from '../../../functions/checkHasErrorInCollapseBlock';
import { CheckboxField } from '../../../UIPackage/MainModule/components/CheckboxField';
import { ErknmEventUav } from '../../../KnmErknmPackage/ErknmEventsModule/components/ErknmEventUav';
import { KnmVersionsEnum } from '../../../APIPackage/KnmModule/enums/KnmVersionsEnum';

interface IPmEventsStoreProps {
  eventTypesSelectOptions: IDictionarySelectOption<IEventType>[] | undefined;
  formRef: IKnmFormRef | undefined;
  interactionCategoryOptions: IInteractionCategorySelectOption[];
  isErp: boolean;
  isNeedActSaveOrSign?: boolean;
  isSupervisor: boolean;
  settings: IAdminSetting[] | undefined;
  startDate: string | undefined;
  statusCode: KnmErpStatusCodesEnum | KnmErknmStatusCodesEnum | undefined;
  stopDate: string | undefined;
}

interface IPmEventsProps {
  className?: string;
  created: string | undefined;
  getBlockId?: (id: string) => string;
  isLinkedByImpossibility?: boolean;
  rescheduling?: IKnmPmFormRescheduling[] | null | undefined;
  watchOnly: boolean;
}

const getDefaultBlockId = (id: string) => id;

const PmEventsWithoutStore = ({
  eventTypesSelectOptions,
  formRef,
  interactionCategoryOptions,
  isErp,
  isNeedActSaveOrSign = false,
  isSupervisor,
  settings,
  startDate,
  statusCode,
  stopDate,
  className,
  created,
  getBlockId = getDefaultBlockId,
  isLinkedByImpossibility,
  rescheduling,
  watchOnly,
}: IPmEventsProps & IPmEventsStoreProps) => {
  const [knmCollapseOpened, setKnmCollapseOpened] = useState<{ [key: string]: boolean }>({});
  const dispatch = useDispatch();

  const push = formRef?.current.mutators.push;
  const remove = formRef?.current.mutators.remove;

  const isNewReschedulingExist = Array.isArray(rescheduling) ? !!rescheduling?.find(i => i.isNew) : false;
  const pmEventsBlockId = getBlockId(PM_EVENTS);
  const isEnablePaperlessDocument = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.ENABLED_PAPERLESS_PASSPORT_4_1_3_STARTS_FROM,
  );

  const [uniqEventTypesSelectOptions, setUniqEventTypesSelectOptions] = useState<
    IDictionaryEventTypeSelectOption[] | undefined
  >(eventTypesSelectOptions);

  const isUnableToRefuse = formRef?.current?.getState().values?.isUnableToRefuse;

  const isEnabled =
    !watchOnly &&
    isSupervisor &&
    (statusCode == null ||
      statusCode === KnmErknmStatusCodesEnum.FILL_IN_PROCESS ||
      (statusCode === KnmErknmStatusCodesEnum.TYPE_WAITING_CARRY_OUT && !isUnableToRefuse));

  // Блокировка документов без учета связи по невозможности проведения предыдущей проверки,
  // по ней доступны статус представления, дата получения и файлы (TASK-9503)
  const isDocumentsSubmissionWatchOnly =
    watchOnly ||
    !isSupervisor ||
    (!!statusCode &&
      statusCode !== KnmErknmStatusCodesEnum.TYPE_WAITING_CARRY_OUT &&
      statusCode !== KnmErknmStatusCodesEnum.FILL_IN_PROCESS) ||
    isNeedActSaveOrSign;

  const isDocumentsWatchOnly = isDocumentsSubmissionWatchOnly || !!isLinkedByImpossibility;

  useEffect(() => {
    updateEventsSelectOptions();
  }, [eventTypesSelectOptions]);

  const addEvent = useCallback(() => {
    if (!watchOnly && push) {
      const addingEvent: IKnmErknmFormEvent = {
        type: undefined,
        interactionCategory: undefined,
        startDate: undefined,
        stopDate: undefined,
        dateCreate: undefined,
        guid: undefined,
        inspectors: undefined,
        isNew: true,
        listId: uuidv4(),
      };

      push('eventsPm', addingEvent);
    }
  }, [push, watchOnly]);

  const makeRemoveEventHandler = (index: number) => () => {
    if (!watchOnly && remove) {
      remove('eventsPm', index);
      formRef?.current.change('isEventDeleted', true);
      updateEventsSelectOptions();
    }
  };

  const updateEventsSelectOptions = () => {
    // @ts-ignore
    const events: IKnmErknmFormEvent[] | undefined = formRef?.current.getFieldState('eventsPm' as keyof IKnmForm)
      ?.value;

    if (!events) {
      setUniqEventTypesSelectOptions(eventTypesSelectOptions);
    } else {
      const eventsWithTypes = events.filter(e => e.type !== undefined);

      if (!eventsWithTypes) {
        setUniqEventTypesSelectOptions(eventTypesSelectOptions);
      } else {
        const selectedTypes: IDictionaryEventTypeSelectOption[] = [];

        eventsWithTypes.forEach(x => (x.type ? selectedTypes.push(x.type) : undefined));

        let uniq = eventTypesSelectOptions;

        // eslint-disable-next-line no-restricted-syntax
        for (const a of selectedTypes) {
          uniq = uniq?.filter(x => x.label !== a.label);
        }

        setUniqEventTypesSelectOptions(uniq);
      }
    }
  };

  const getInteractionCategoryOptions = () => {
    if (formRef?.current.getFieldState('interactionCategory')?.value?.label?.includes('1')) {
      return interactionCategoryOptions?.filter(x => x.label?.includes('1'));
    }
    if (formRef?.current.getFieldState('interactionCategory')?.value?.label?.includes('3')) {
      return interactionCategoryOptions?.filter(x => x.label?.includes('3'));
    }

    return interactionCategoryOptions;
  };

  const handleSetEventType = (eventFieldName: string) => () => {
    formRef?.current.change(`${eventFieldName}.interactionCategory` as keyof IKnmPmForm);
    formRef?.current.change(`${eventFieldName}.startDate` as keyof IKnmPmForm);
    formRef?.current.change(`${eventFieldName}.stopDate` as keyof IKnmPmForm);
    formRef?.current.change(`${eventFieldName}.uavTechnologies` as keyof IKnmPmForm);
    updateEventsSelectOptions();
  };

  const onChangeIsUavUsed = (eventFieldName: string, e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.checked) {
      // @ts-ignore
      formRef?.current.change(`${eventFieldName}.uavTechnologies`);
    }
  };

  useEffect(() => {
    dispatch(catalogGet(CommonCatalogsNamesEnum.INTERACTION_CATEGORIES));
  }, [dispatch]);

  const validateArray = () => {
    return undefined;

    // Убрано по задаче TEST-TASK-3672 Неверное отображение раздела “Перечень действий, осуществляемых в рамках ПМ” в красном восклицательном знаке
    // https://pulse.dsdqd.ru/entries/TEST-TASK-3672
    /* if (isNew) return undefined;
    const isUnableToRefuseParam = formRef?.current?.getState().values?.isUnableToRefuse;

    if (isUnableToRefuseParam && (!events || !events.length)) {
      return 'Поле не может быть пустым';
    } */
  };

  const parseStartDate = parseDate({ date: startDate || '', parseFormat: DATE_FORMAT_LONG_FNS });
  const parseStopDate = parseDate({ date: stopDate || '', parseFormat: DATE_FORMAT_LONG_FNS });

  const lastRescheduling =
    Array.isArray(rescheduling) && rescheduling.length > 0 ? rescheduling[rescheduling.length - 1] : undefined;

  const startDateLastRescheduling = lastRescheduling?.newStartDate;
  const stopDateLastRescheduling = lastRescheduling?.newEndDate;
  const newParseStartDate = parseDate({ date: startDateLastRescheduling || '', parseFormat: DATE_FORMAT_LONG_FNS });
  const newParseStopDate = parseDate({ date: stopDateLastRescheduling || '', parseFormat: DATE_FORMAT_LONG_FNS });

  return (
    <div className={className ? `${styles.Wrapper} ${className}` : styles.Wrapper} id={pmEventsBlockId}>
      <div>
        <h2 className="shared-title__with-margin">Перечень действий</h2>
        <div className="shared-group-label_margin">
          {getTitleFieldWithRedStar({
            label: 'Перечень действий, осуществляемых в рамках ПМ',
            sectionName: 'eventsPm',
            field: `eventsPm`,
            isPm: true,
          })}

          {!isLinkedByImpossibility && (
            <Button
              className="shared-btn-add"
              text="Добавить"
              variant="link"
              color="primary"
              type="button"
              id="pmEventsAddButton"
              disabled={!isEnabled}
              onClick={addEvent}
            />
          )}
        </div>
      </div>
      {isLinkedByImpossibility && formRef?.current.getState()?.values.isEventDeleted && (
        <div className={styles.Yellow}>
          Для добавления сведений в раздел необходимо удалить и повторно добавить ссылку на паспорт
        </div>
      )}
      <FormSpy subscription={{ errors: true, touched: true }}>
        {props => {
          const fieldError = props.errors?.eventsPm?.[ARRAY_ERROR];
          const isFieldTouched = props.touched?.eventsPm;

          if (isFieldTouched && !Array.isArray(fieldError) && fieldError) {
            return <p className="shared-input-error shared-row__small-margin">{fieldError}</p>;
          }
          if (isFieldTouched && Array.isArray(fieldError) && fieldError) {
            return <p className="shared-input-error shared-row__small-margin">{fieldError[0]}</p>;
          }

          return null;
        }}
      </FormSpy>
      <FieldArray name="eventsPm" validate={() => validateArray()}>
        {({ fields }: FieldArrayRenderProps<IKnmErknmFormEvent, HTMLElement>) => {
          if (!fields.length) {
            return null;
          }

          return (
            <ul className={styles.EventsList}>
              {fields.map((eventsFieldName, sIndex) => {
                const eventsFieldsValue: IKnmErknmFormEvent[] = fields.value;
                const eventsFieldValue: IKnmErknmFormEvent = eventsFieldsValue?.[sIndex];
                const id = eventsFieldValue?.listId ? getEventBlockId(eventsFieldValue?.listId) : `pm-event-${sIndex}`;
                const { isFilledFromLink } = eventsFieldValue;
                const isEnabledAndNotFilledFromLink = isEnabled && !isFilledFromLink;

                const inspectionResult = eventsFieldValue.type?.inspectionResult;

                const hasError = checkHasErrorInCollapseBlock(formRef, eventsFieldName);

                const { isUavUsed, type } = eventsFieldValue;

                const isInspection = type ? type.label === 'Осмотр' : undefined;
                const isSampling = type ? type.label === 'Отбор проб (образцов)' : undefined;

                const isShowUav = !isUnableToRefuse && (isInspection || isSampling);

                const parseStartDateEventValue = parseDate({
                  date: eventsFieldValue.startDate || '',
                  parseFormat: DATE_FORMAT_LONG_FNS,
                });

                const startDateEventValue = eventsFieldValue?.startDate;
                const stopDateEventValue = eventsFieldValue?.stopDate;

                return (
                  <KnmCollapse
                    key={id}
                    id={id}
                    title={`Действие №${sIndex + 1}`}
                    subtitle={eventsFieldValue?.type?.label}
                    isNew={eventsFieldValue?.isNew}
                    openByDefault={eventsFieldValue?.isNew}
                    onClose={
                      !watchOnly && (isEnabled || isLinkedByImpossibility) ? makeRemoveEventHandler(sIndex) : undefined
                    }
                    changeOpenedCallback={e =>
                      setKnmCollapseOpened(state => ({
                        ...state,
                        [eventsFieldName]: e,
                      }))
                    }
                    headerClassName={cn([
                      {
                        [styles.BorderYellow]:
                          inspectionResult &&
                          inspectionResult.resultActionType === InspectionResultActionTypeEnum.LIGHT_YELLOW,
                        [styles.BorderRed]:
                          inspectionResult &&
                          inspectionResult.resultActionType === InspectionResultActionTypeEnum.LIGHT_RED,
                      },
                    ])}
                    error={hasError && knmCollapseOpened[eventsFieldName]}
                  >
                    <>
                      <Row>
                        <FieldBlock>
                          <SelectInputField
                            placeholder={getTitleFieldWithRedStar({
                              label: 'Выберите действие',
                              field: `${eventsFieldName}.type`,
                              isPm: true,
                            })}
                            id={`${eventsFieldName}.type`}
                            name={`${eventsFieldName}.type`}
                            options={uniqEventTypesSelectOptions}
                            size="large"
                            onChange={handleSetEventType(eventsFieldName)}
                            disabled={!isEnabledAndNotFilledFromLink || isLinkedByImpossibility}
                          />
                        </FieldBlock>
                      </Row>
                      <Row>
                        <FieldBlock>
                          <SelectInputField
                            placeholder={getTitleFieldWithRedStar({
                              label: 'Выберите категорию взаимодействия',
                              field: `${eventsFieldName}.interactionCategory`,
                              isPm: true,
                            })}
                            id={`${eventsFieldName}.interactionCategory`}
                            name={`${eventsFieldName}.interactionCategory`}
                            options={getInteractionCategoryOptions()}
                            size="large"
                            disabled={!isEnabledAndNotFilledFromLink}
                          />
                        </FieldBlock>
                      </Row>
                      <Row>
                        <FieldBlock size="half">
                          <DatePickerField
                            placeholder={getTitleFieldWithRedStar({
                              label: 'Дата начала действия',
                              field: `${eventsFieldName}.startDate`,
                              isPm: true,
                            })}
                            id={`${eventsFieldName}.startDate`}
                            name={`${eventsFieldName}.startDate`}
                            disabled={isNewReschedulingExist ? false : !isEnabled}
                            minDate={newParseStartDate || parseStartDate}
                            maxDate={newParseStopDate || parseStopDate}
                          />
                        </FieldBlock>
                        <FieldBlock size="half">
                          <DatePickerField
                            placeholder={getTitleFieldWithRedStar({
                              label: 'Дата окончания действия',
                              field: `${eventsFieldName}.stopDate`,
                              isPm: true,
                            })}
                            id={`${eventsFieldName}.stopDate`}
                            name={`${eventsFieldName}.stopDate`}
                            disabled={isNewReschedulingExist ? false : !isEnabled}
                            minDate={parseStartDateEventValue ?? (newParseStartDate || parseStartDate)}
                            maxDate={newParseStopDate || parseStopDate}
                          />
                        </FieldBlock>
                      </Row>
                      {isEnablePaperlessDocument &&
                        (eventsFieldValue?.type?.label || '').indexOf('Истребование документов') > -1 && (
                          <KnmEventsDocuments
                            sIndex={sIndex}
                            formRef={formRef}
                            eventFieldName={eventsFieldName}
                            isWatchOnly={isDocumentsWatchOnly}
                            isSubmissionWatchOnly={isDocumentsSubmissionWatchOnly}
                            baseId={id}
                            isPm
                            startDateEvent={startDateEventValue}
                            stopDateEvent={stopDateEventValue}
                            isFilledFromLink={isFilledFromLink === true}
                          />
                        )}
                      {isShowUav && (
                        <Row>
                          {!isErp && (
                            <FieldBlock>
                              <CheckboxField
                                className={styles.FilterFormGroupCheckbox}
                                id={`${id}.isUavUsed`}
                                name={`${eventsFieldName}.isUavUsed`}
                                label="Проводились с использованием беспилотных технологий"
                                theme="system"
                                type="checkbox"
                                size="medium"
                                disabled={!isEnabled}
                                onChangeCallback={e => onChangeIsUavUsed(eventsFieldName, e)}
                              />
                            </FieldBlock>
                          )}
                          {isUavUsed && (
                            <ErknmEventUav
                              id={`${id}.uavTechnologies`}
                              name={`${eventsFieldName}.uavTechnologies`}
                              watchOnly={!isEnabled}
                              isPm={true}
                            />
                          )}
                        </Row>
                      )}
                    </>
                  </KnmCollapse>
                );
              })}
            </ul>
          );
        }}
      </FieldArray>
    </div>
  );
};

const mapStateToProps = (state: IRootState): IPmEventsStoreProps => ({
  formRef: getKnmFormRef(state),
  statusCode: getKnmStatusCode(state),
  settings: getAdminFetchSettings(state),
  isSupervisor: getUserRoleIsSupervisor(state),
  eventTypesSelectOptions: getKnmPmProfEventTypesSelectOptions(state),
  interactionCategoryOptions: getCatalogsInteractionCategorySelectOptions(state),
  isNeedActSaveOrSign: getKnmIsNeedActSaveOrSign(state),
  startDate: getPmStartDate(state),
  stopDate: getPmStopDate(state),
  isErp: getKnmDataVersion(state) === KnmVersionsEnum.ERP,
});

export const PmEvents = connect(mapStateToProps)(PmEventsWithoutStore);
